import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Map, fromJS } from 'immutable';
import CheckSheet from 'src/screen/Protected/DeviceScreen/TestingReport/CheckSheet';
import FinalizeAndSignOff from 'src/screen/Protected/DeviceScreen/TestingReport/FinalizeAndSignOff';
import FieldReportModal from 'src/screen/Protected/DeviceScreen/Commissioning/FieldReportModal';
import NotesField from 'src/screen/Protected/DeviceScreen/Commissioning/NotesField';
import CircuitDynamicTable from 'src/screen/Protected/DeviceScreen/CommissioningCircuit/CircuitDynamicTable';
import { useTranslation } from 'src/i18n';
import ProjectInfoTable from 'src/screen/Protected/DeviceScreen/Commissioning/ProjectInfoTable';
import PanelAuditFieldWireTests from './PanelAuditFieldWireTests';

const REPORT_TYPE = 'audit-panel';
// Two task types so §2 and §4 render as separately titled tables, as on the paper form.
const EXTERNAL_TYPE = 'audit-panel-external';
const INTERNAL_TYPE = 'audit-panel-internal';
const TASK_TYPES = [EXTERNAL_TYPE, INTERNAL_TYPE];

// Device columns copied into the form when a device is linked, if still empty.
const PREFILL_FIELDS = [
  { path: ['projectInfo', 'panelTag'], deviceKey: 'tag' },
  { path: ['projectInfo', 'unitArea'], deviceKey: 'unit' },
];

// §1's fields in the paper form's reading order; ProjectInfoTable lays them out two pairs
// per row.
const PROJECT_INFO_FIELDS = [
  'panelTag', 'unitArea',
  'panelSerialNumber', 'transformerRating',
  'primaryVoltageRating', 'controllingDocument',
  'secondaryVoltageRating', 'panelType',
];

// §8's instruments, named by the paper form. Labels come from i18n.
const TESTING_DEVICE_KEYS = ['multimeter', 'megohmmeter'];

export default function PanelAuditReport (props) {
  const { t } = useTranslation();

  const tabs = useMemo(() => [
    { value: '1', label: t('panelAudit.tabs.projectInfo') },
    { value: '2', label: t('panelAudit.tabs.external') },
    { value: '3', label: t('panelAudit.tabs.internal') },
    { value: '4', label: t('panelAudit.tabs.tests') },
    { value: '5', label: t('panelAudit.tabs.signOff') },
  ], [t]);

  // §5 is two tables because its rows are headed differently: the first two measure phases
  // A/B/C, the third measures the pairs between them. A dynamic table has one header row.
  const measurementColumn = useMemo(
    () => ({ key: 'measurement', label: t('panelAudit.mainBreakerTests.measurement'), width: '40%', locked: true }),
    [t],
  );

  const phaseColumns = useMemo(() => [
    measurementColumn,
    { key: 'a', label: t('panelAudit.mainBreakerTests.phaseA') },
    { key: 'b', label: t('panelAudit.mainBreakerTests.phaseB') },
    { key: 'c', label: t('panelAudit.mainBreakerTests.phaseC') },
  ], [t, measurementColumn]);

  const phasePairColumns = useMemo(() => [
    measurementColumn,
    { key: 'a', label: t('panelAudit.mainBreakerTests.aToB') },
    { key: 'b', label: t('panelAudit.mainBreakerTests.aToC') },
    { key: 'c', label: t('panelAudit.mainBreakerTests.bToC') },
  ], [t, measurementColumn]);

  // The two instruments are fixed by the form, so the device column is locked and there is no
  // add or delete — the same shape as the HMS audit's Equipment Used table.
  const testingDeviceColumns = useMemo(() => [
    { key: 'device', label: t('panelAudit.testingDevices.device'), width: '30%', locked: true },
    { key: 'idNumber', label: t('panelAudit.testingDevices.idNumber') },
    { key: 'calDueDate', label: t('panelAudit.testingDevices.calDueDate'), type: 'date' },
  ], [t]);

  const buildEmptyResults = useCallback((tasksByType, device) => {
    const emptyTasks = (type: string) => {
      const tasks = tasksByType.get(type);
      const tasksList = tasks?.toJS ? tasks.toJS() : [];
      // Raw redux rows use `task`; the getCheckSheetTasks selector renames it to `key`.
      return tasksList.map((task: any) => ({ task_id: task.task ?? task.key, status: null, comment: '' }));
    };
    const emptyWireTest = {
      circuitBreakerNumber: '', controllerNumber: '', voltage: '', current: '', comments: '',
    };
    const measurementRow = (key: string) => ({
      measurement: t(`panelAudit.mainBreakerTests.${key}`), a: '', b: '', c: '',
    });

    return fromJS({
      info: { type: REPORT_TYPE, deviceId: props.deviceId || null },
      [EXTERNAL_TYPE]: emptyTasks(EXTERNAL_TYPE),
      [INTERNAL_TYPE]: emptyTasks(INTERNAL_TYPE),
      extras: {
        projectInfo: {
          panelTag: device?.get('tag') || '',
          unitArea: device?.get('unit') || '',
          panelSerialNumber: '',
          transformerRating: '',
          primaryVoltageRating: '',
          controllingDocument: '',
          secondaryVoltageRating: '',
          panelType: '',
        },
        externalInspection: { comments: '' },
        // Split by how the columns are headed: phases A/B/C, then the pairs between them.
        mainBreakerTests: {
          byPhase: [measurementRow('phaseCurrent'), measurementRow('phaseToGround')],
          byPhasePair: [measurementRow('phaseToPhase')],
        },
        // The paper form prints a fixed block of blank rows; four is enough to start on and
        // the table grows, so the report carries only the circuits actually tested.
        fieldWireTests: { items: [emptyWireTest, emptyWireTest, emptyWireTest, emptyWireTest] },
        internalTestComments: { notes: '' },
        // The paper form names these two instruments; seeded so the labels match it.
        testingDevices: {
          items: TESTING_DEVICE_KEYS.map(key => ({
            device: t(`panelAudit.testingDevices.${key}`), idNumber: '', calDueDate: '',
          })),
        },
      },
      signOff: {},
    }) as Map<string, any>;
  }, [props.deviceId, t]);

  const renderTab = useCallback((tabValue, ctx) => {
    const { allResults, extras, change, readOnly, linkedDeviceTag, handleUnlinkDevice, handleDeviceSelect } = ctx;
    switch (tabValue) {
      case '1':
        return (
          <ProjectInfoTable
            fields={PROJECT_INFO_FIELDS}
            i18nPrefix='panelAudit'
            tagField='panelTag'
            readOnly={readOnly}
            data={extras}
            onChange={change}
            linkedDeviceTag={linkedDeviceTag}
            onUnlink={handleUnlinkDevice}
            onDeviceSelect={handleDeviceSelect}
          />
        );
      case '2':
        // §3's comments box sits under §2 because several of its questions say
        // "If yes, record in comments".
        return (
          <>
            <CheckSheet
              readOnly={readOnly}
              onChange={change}
              id={EXTERNAL_TYPE}
              title={t('panelAudit.externalInspectionItems')}
              data={allResults?.get(EXTERNAL_TYPE)}
              checkSheetType={EXTERNAL_TYPE}
            />
            <NotesField
              title={t('panelAudit.externalInspectionComments')}
              value={extras?.getIn(['externalInspection', 'comments'])}
              path={['extras', 'externalInspection', 'comments']}
              onChange={change}
              readOnly={readOnly}
            />
          </>
        );
      case '3':
        return (
          <CheckSheet
            readOnly={readOnly}
            onChange={change}
            id={INTERNAL_TYPE}
            title={t('panelAudit.internalInspectionItems')}
            data={allResults?.get(INTERNAL_TYPE)}
            checkSheetType={INTERNAL_TYPE}
          />
        );
      case '4':
        return (
          <>
            <CircuitDynamicTable
              readOnly={readOnly}
              title={t('panelAudit.mainBreakerTests.title')}
              columns={phaseColumns}
              data={extras?.getIn(['mainBreakerTests', 'byPhase'])}
              extrasKey={['mainBreakerTests', 'byPhase']}
              onChange={change}
              addable={false}
            />
            <CircuitDynamicTable
              readOnly={readOnly}
              title={t('panelAudit.mainBreakerTests.phaseToPhaseTitle')}
              columns={phasePairColumns}
              data={extras?.getIn(['mainBreakerTests', 'byPhasePair'])}
              extrasKey={['mainBreakerTests', 'byPhasePair']}
              onChange={change}
              addable={false}
            />
            <PanelAuditFieldWireTests readOnly={readOnly} data={extras} onChange={change} />
          </>
        );
      case '5':
        return (
          <>
            <CircuitDynamicTable
              readOnly={readOnly}
              title={t('panelAudit.testingDevices.title')}
              columns={testingDeviceColumns}
              data={extras?.getIn(['testingDevices', 'items'])}
              extrasKey={['testingDevices', 'items']}
              onChange={change}
              addable={false}
            />
            {/* §9's named representatives are dropped: the account that submitted the report
                is recorded here instead. §7 covers the internal and test sections
                collectively, so it is the sign-off notes box. */}
            <FinalizeAndSignOff
              readOnly={readOnly}
              data={allResults?.get('signOff')}
              onChange={change}
              notesLabel={t('panelAudit.internalTestComments')}
              notesValue={extras?.getIn(['internalTestComments', 'notes'])}
              notesPath={['extras', 'internalTestComments', 'notes']}
            />
          </>
        );
      default:
        return null;
    }
  }, [t, phaseColumns, phasePairColumns, testingDeviceColumns]);

  return (
    <FieldReportModal
      reportType={REPORT_TYPE}
      taskTypes={TASK_TYPES}
      tabs={tabs}
      title={{ label: t('panelAudit.modalTitle') }}
      prefillFields={PREFILL_FIELDS}
      requiresStatus={false}
      buildEmptyResults={buildEmptyResults}
      renderTab={renderTab}
      deviceId={props.deviceId}
      reportId={props.reportId}
      open={props.open}
      handleClose={props.handleClose}
    />
  );
}

PanelAuditReport.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  reportId: PropTypes.string,
};
